// Wave 9 — Click drill-down log page.
//
// Lists `/api/clicks/log` rows in a sortable table with simple filter
// inputs and infinite-scroll cursor pagination. Selecting a row opens a
// modal with the full raw click record so the operator can inspect the
// context (geo, device, UA, stream / variant decision) without bouncing
// to Mongo Compass.

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { useT } from '../i18n';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Check, Columns as ColumnsIcon } from 'lucide-react';

const PAGE_SIZE = 50;

function formatTs(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch (_e) {
    return String(value);
  }
}

const EMPTY_FILTERS = {
  campaign_id: '',
  stream_id: '',
  country: '',
  device: '',
  ip: '',
  cloaked: '',
  from: '',
  to: '',
};

// Columns hidden until the user turns them on, so the table fits a laptop
// screen without sideways scrolling. The Columns menu shows all of them.
const DEFAULT_HIDDEN = JSON.stringify(['stream_id', 'empty_ref', 'unique', 'lp_clicked', 'lp_seconds']);

export default function ClicksLog() {
  const { t } = useT();
  const location = useLocation();
  // Seed filters from the URL query string once, so a "drill into log" jump
  // from Reports (e.g. /clicks/log?campaign_id=…&country=US&from=…) lands
  // pre-filtered. No params → all blank, identical to the standalone page.
  const initialFilters = useMemo(() => {
    const base = { ...EMPTY_FILTERS };
    try {
      const sp = new URLSearchParams(location.search);
      Object.keys(base).forEach((k) => {
        const v = sp.get(k);
        if (v != null) base[k] = v;
      });
    } catch (_e) {
      /* ignore malformed query string */
    }
    return base;
  }, []);
  const [filters, setFilters] = useState(initialFilters);
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [hiddenCols, setHiddenCols] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem('xpnsr.clicksLog.hidden') || DEFAULT_HIDDEN));
    } catch (_e) {
      return new Set();
    }
  });
  const toggleCol = (key) =>
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try {
        localStorage.setItem('xpnsr.clicksLog.hidden', JSON.stringify([...next]));
      } catch (_e) {
        /* ignore quota / private-mode errors */
      }
      return next;
    });

  const [campaigns, setCampaigns] = useState([]);
  useEffect(() => {
    axios
      .get(API + '/campaigns')
      .then((r) => setCampaigns(r.data || []))
      .catch(() => {});
  }, []);
  const campaignName = useMemo(() => {
    const map = {};
    campaigns.forEach((c) => {
      map[c.id] = c.name;
    });
    return map;
  }, [campaigns]);

  const COLUMNS = [
    { key: 'timestamp', label: t('Time'), value: (r) => (r.timestamp ? new Date(r.timestamp).toISOString() : ''), render: (r) => formatTs(r.timestamp) },
    { key: 'campaign_id', label: t('Campaign'), value: (r) => campaignName[r.campaign_id] || r.campaign_id || '' },
    { key: 'stream_id', label: t('Stream'), value: (r) => r.stream_id || '' },
    { key: 'country', label: t('Country'), value: (r) => r.country_code || '', render: (r) => (r.country_code ? <Badge variant="outline">{r.country_code}</Badge> : '—') },
    { key: 'device', label: t('Device'), value: (r) => r.device_type || '' },
    { key: 'ip', label: t('IP'), value: (r) => r.ip_address || '' },
    { key: 'browser', label: t('Browser'), value: (r) => r.browser || '' },
    { key: 'os', label: t('OS'), value: (r) => r.os || '' },
    { key: 'isp', label: t('ISP / provider'), value: (r) => r.isp || '' },
    { key: 'proxy', label: t('Proxy'), value: (r) => (r.is_proxy ? t('yes') : '') },
    { key: 'cloaked', label: t('Cloaked'), value: (r) => (r.cloaked ? (r.cloak_reason || t('yes')) : ''), render: (r) => (r.cloaked ? <Badge variant="outline">{r.cloak_reason || t('yes')}</Badge> : '—') },
    { key: 'empty_ref', label: t('Empty referrer'), value: (r) => (r.referer ? '' : t('yes')) },
    { key: 'unique', label: t('Unique'), value: (r) => (r.is_unique ? t('yes') : t('no')) },
    { key: 'lp_clicked', label: t('Landing click'), value: (r) => (r.lp_clicked ? t('yes') : '') },
    { key: 'lp_seconds', label: t('Time on landing'), value: (r) => (r.lp_seconds != null ? r.lp_seconds + ' ' + t('sec') : '') },
  ];
  const visibleColumns = COLUMNS.filter((c) => !hiddenCols.has(c.key));

  const filterParams = useMemo(() => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v) params[k] = v;
    });
    params.limit = PAGE_SIZE;
    return params;
  }, [filters]);

  const fetchPage = async ({ append }) => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API}/clicks/log`, {
        params: append && cursor ? { ...filterParams, cursor } : filterParams,
      });
      const data = response.data || {};
      setHasMore(Boolean(data.has_more));
      setCursor(data.next_cursor || null);
      setRows((prev) => (append ? [...prev, ...(data.rows || [])] : data.rows || []));
    } catch (e) {
      setError(e?.response?.data?.detail || t('Failed to load clicks log'));
    } finally {
      setLoading(false);
    }
  };

  // Reset page when filters change (the user explicitly hit "Apply").
  const applyFilters = () => {
    setCursor(null);
    setRows([]);
    fetchPage({ append: false });
  };

  // Initial load — fetch the first page once on mount. Subsequent reloads
  // happen when the user explicitly clicks "Apply" on the filter form.
  useEffect(() => {
    applyFilters();
  }, []);

  const exportCSV = useCallback(() => {
    if (rows.length === 0) return;
    const activeCols = COLUMNS.filter((c) => !hiddenCols.has(c.key));
    const esc = (v) => {
      const s = String(v ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    };
    const header = activeCols.map((c) => c.label).join(',');
    const body = rows
      .map((r) => activeCols.map((c) => esc(c.value(r))).join(','))
      .join('\n');
    const blob = new Blob([header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clicks-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [rows]);

  return (
    <div className="xp-page">
      <Card data-testid="clicks-log-card">
        <CardHeader>
          <CardTitle>{t('Clicks log')}</CardTitle>
          <CardDescription>
            {t(
              'Row-level drill-down into incoming clicks: country, device, stream, and variant.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div>
              <Label>{t('Campaign')}</Label>
              <Select
                value={filters.campaign_id || 'all'}
                onValueChange={(v) =>
                  setFilters((f) => ({ ...f, campaign_id: v === 'all' ? '' : v }))
                }
              >
                <SelectTrigger data-testid="filter-campaign-id">
                  <SelectValue placeholder={t('All campaigns')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('All campaigns')}</SelectItem>
                  {campaigns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('Stream ID')}</Label>
              <Input
                data-testid="filter-stream-id"
                value={filters.stream_id}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, stream_id: e.target.value }))
                }
              />
            </div>
            <div>
              <Label>{t('Country (ISO-2)')}</Label>
              <Input
                data-testid="filter-country"
                value={filters.country}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, country: e.target.value }))
                }
              />
            </div>
            <div>
              <Label>{t('Device')}</Label>
              <Input
                data-testid="filter-device"
                value={filters.device}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, device: e.target.value }))
                }
              />
            </div>
            <div>
              <Label>{t('IP')}</Label>
              <Input
                data-testid="filter-ip"
                value={filters.ip}
                onChange={(e) => setFilters((f) => ({ ...f, ip: e.target.value }))}
              />
            </div>
            <div>
              <Label>{t('Cloaked')}</Label>
              <Select
                value={filters.cloaked || 'all'}
                onValueChange={(v) =>
                  setFilters((f) => ({ ...f, cloaked: v === 'all' ? '' : v }))
                }
              >
                <SelectTrigger data-testid="filter-cloaked">
                  <SelectValue placeholder={t('All traffic')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('All traffic')}</SelectItem>
                  <SelectItem value="true">{t('Cloaked only')}</SelectItem>
                  <SelectItem value="false">{t('Real only')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('From (ISO-8601)')}</Label>
              <Input
                data-testid="filter-from"
                value={filters.from}
                onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
                placeholder="2026-05-01T00:00:00Z"
              />
            </div>
            <div>
              <Label>{t('To (ISO-8601)')}</Label>
              <Input
                data-testid="filter-to"
                value={filters.to}
                onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
                placeholder="2026-05-02T00:00:00Z"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            <Button
              type="button"
              onClick={applyFilters}
              data-testid="apply-filters"
              disabled={loading}
            >
              {t('Apply')}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setFilters({ ...EMPTY_FILTERS });
              }}
              data-testid="reset-filters"
            >
              {t('Reset')}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" style={{ marginLeft: 'auto' }}>
                  <ColumnsIcon className="w-4 h-4 mr-2" />
                  {t('Columns')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
                <DropdownMenuLabel>{t('Columns')}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {COLUMNS.map((c) => (
                  <DropdownMenuItem
                    key={c.key}
                    onSelect={(e) => {
                      e.preventDefault();
                      toggleCol(c.key);
                    }}
                  >
                    <Check className={'mr-2 h-4 w-4 ' + (hiddenCols.has(c.key) ? 'opacity-0' : 'opacity-100')} />
                    {c.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {rows.length > 0 && (
              <Button
                type="button"
                variant="outline"
                onClick={exportCSV}
                data-testid="export-csv"
                style={{ marginLeft: '0.5rem' }}
              >
                {t('Export CSV')}
              </Button>
            )}
          </div>

          {error && (
            <div className="xp-error" data-testid="clicks-log-error">
              {error}
            </div>
          )}

          <Table className="xp-sticky-first" data-testid="clicks-log-table">
            <TableHeader>
              <TableRow>
                {visibleColumns.map((c) => (
                  <TableHead key={c.key}>{c.label}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.id || `${row.click_id}-${row.timestamp}`}
                  className="cursor-pointer"
                  onClick={() => setSelected(row)}
                  data-testid={`clicks-row-${row.id}`}
                >
                  {visibleColumns.map((c) => (
                    <TableCell key={c.key}>
                      {c.render ? c.render(row) : (c.value(row) || '—')}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {rows.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} className="text-center text-muted-foreground">
                    {t('No clicks match these filters.')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <div className="flex justify-center mt-4">
            {hasMore && (
              <Button
                type="button"
                variant="outline"
                onClick={() => fetchPage({ append: true })}
                disabled={loading}
                data-testid="load-more"
              >
                {loading ? t('Loading…') : t('Load more')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {selected && (
        <div
          className="xp-modal-backdrop"
          onClick={() => setSelected(null)}
          data-testid="clicks-detail-modal"
        >
          <div className="xp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="xp-modal-h">
              <h3>{t('Click detail')}</h3>
              <button
                type="button"
                onClick={() => setSelected(null)}
                aria-label={t('Close')}
                data-testid="close-detail"
              >
                ×
              </button>
            </div>
            <pre className="xp-modal-body">
              {JSON.stringify(selected, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
